import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable } from 'rxjs';
import { tap, map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { Address } from '@classes/address';

@Injectable({
  providedIn: 'root'
})
export class AddressesService {
  addressesListChanged = new Subject<Address[]>();
  private baseUrl: string = environment.apiUrl + 'addresses';
  private addresses: Address[];
  private senders: Address[];
  private receivers: Address[];

  constructor(
    private http: HttpClient
  ) { }

  getList(): Observable<Address[]> {
    return this.http.get<Address[]>(this.baseUrl)
      .pipe(
        map(addresses => {
          this.addresses = addresses.map(c => Address.fromObject(c));
          this.addresses.sort((a, b) => a.name.localeCompare(b.name));
          this.addressesListChanged.next([...this.addresses]);
          return [...this.addresses];
        })
      );
  }

  getListForSite(siteId: string): Observable<Address[]> {
    return this.http.get<Address[]>(`${environment.apiUrl}sites/${siteId}/addresses`)
      .pipe(
        map(addresses => {
          this.addresses = addresses.map(c => Address.fromObject(c));
          this.addresses.sort((a, b) => a.name.localeCompare(b.name));

          return [...this.addresses];
        })
      );

  }

  getSenderList(): Observable<Address[]> {
    return this.http.get<Address[]>(this.baseUrl)
      .pipe(
        map(addresses => {
          this.senders = addresses.map(c => Address.fromObject(c)).filter(address => address.isSender);
          this.addressesListChanged.next([...this.senders]);
          return [...this.senders];
        })
      );
  }

  getReceiverList(): Observable<Address[]> {
    return this.http.get<Address[]>(this.baseUrl)
      .pipe(
        map(addresses => {
          this.receivers = addresses.map(c => Address.fromObject(c)).filter(address => address.isReceiver);
          this.addressesListChanged.next([...this.receivers]);
          return [...this.receivers];
        })
      );
  }

  put(client: Address): Observable<Address> {
    return this.http.put<Address>(this.baseUrl, this.toPayload(client))
      .pipe(
        tap(editedAddress => {
          editedAddress = Address.fromObject(editedAddress);
          this.addresses = this.addresses.map(c => c.id === editedAddress.id ? editedAddress : c);
          this.addressesListChanged.next([...this.addresses]);
        })
      );
  }

  post(client: Address): Observable<Address> {
    return this.http.post<Address>(this.baseUrl, this.toPayload(client))
      .pipe(
        tap(createdAddress => {
          createdAddress = Address.fromObject(createdAddress);
          this.addresses.push(createdAddress);
          this.addressesListChanged.next([...this.addresses]);
        })
      );
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`)
      .pipe(
        tap(() => {
          this.addresses = this.addresses.filter(m => m.id !== id);
          this.addressesListChanged.next([...this.addresses]);
        })
      );
  }

  private toPayload(address: Address) {
    const { addressGroups, ...payload } = address;
    return payload;
  }
}
